import * as React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Button } from "./button.js";
import { CheckboxList, type CheckboxListOption } from "./checkbox-list.js";
import { Popover, PopoverContent, PopoverTrigger } from "./popover.js";
import { selectPanelClasses, selectTriggerClasses } from "./select.js";

// A Select that picks several values: the closed field looks exactly like
// ui/select, and opening it shows a searchable checkbox list (ui/checkbox-list)
// in the dropdown. Use it where a form row needs a multi-choice that should sit
// in line with ordinary Select fields; use a bare CheckboxList when the list
// itself is the main content (e.g. picking many employees).
//
// Ticks inside the open list are a draft: OK applies them, Cancel (or closing
// the list any other way, e.g. clicking outside) discards them.

export interface MultiSelectProps {
  options: CheckboxListOption[];
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
  /** Shown when nothing is selected. */
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  emptyText?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
  id?: string | undefined;
}

// Up to this many names are listed in the closed field; beyond it, a count.
const MAX_NAMED = 2;

function MultiSelect({
  options,
  selected,
  onSelectedChange,
  placeholder = "Select…",
  searchPlaceholder,
  emptyText,
  disabled,
  className,
  id,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<Set<string>>(selected);

  function openChange(next: boolean) {
    // Every opening starts from the applied selection; any close that is not
    // OK leaves `selected` untouched, which is what makes it a cancel.
    if (next) setDraft(new Set(selected));
    setOpen(next);
  }

  function apply() {
    onSelectedChange(draft);
    setOpen(false);
  }

  const chosen = options.filter((o) => selected.has(o.value));
  const summary =
    chosen.length === 0
      ? null
      : chosen.length <= MAX_NAMED
        ? chosen.map((o) => o.label).join(", ")
        : `${chosen.length} selected`;

  return (
    // `modal` so the list stays wheel-scrollable inside a modal Dialog, as in ui/combobox.
    <Popover modal open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(selectTriggerClasses, "text-left", className)}
        >
          <span className={cn("truncate", !summary && "text-surface-400")}>{summary ?? placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-surface-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn(selectPanelClasses, "w-[var(--radix-popover-trigger-width)] min-w-[12rem] p-0")}>
        <CheckboxList
          className="rounded-none border-0"
          options={options}
          selected={draft}
          onSelectedChange={setDraft}
          searchPlaceholder={searchPlaceholder}
          emptyText={emptyText}
        />
        <div className="flex items-center justify-end gap-2 border-t border-surface-100 p-2">
          <Button variant="ghost" size="sm" onClick={() => openChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={apply}>
            OK
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { MultiSelect };
